import { useEffect } from 'react';

export const useMountEffect = (fun) => useEffect(fun, []);
export const range = (n) => [...Array(n).keys()];
export const flattened = (arr) => [].concat(...arr);
export const repeatNTimesIntoFlatArray = (n, elems) => flattened(range(n).map(() => elems));

// note: this method is slow for big dataasets, O(n)
export const unique = (arr) => arr.filter((v, i, a) => a.indexOf(v) === i);
export const twoFixedDecimals = (num) => {
  const rounded = Math.round((num + Number.EPSILON) * 100) / 100;
  const fixed = parseFloat(rounded).toFixed(2);
  return fixed;
};

export const formatPercentage = (perc) => {
  if (perc === 'N/A') {
    return perc;
  }
  return !perc ? '0.00' : twoFixedDecimals(perc);
};

export const zeroIfNaN = (num) => (isNaN(num) ? 0 : num);

export const isThisLastYearValidForPercentageDiff = (thisYear, lastYear) => !!(thisYear && thisYear !== 0) && !!(lastYear && lastYear !== 0);

export const formatAmount = (amount) => {
  const divByDecimal = (`${amount}`).split('.');
  const decimalPart = divByDecimal[1];
  const nonDecimalPart = divByDecimal[0];

  const l = nonDecimalPart.length;
  if (l < 5) {
    return `${nonDecimalPart}${
      decimalPart && decimalPart.length ? `.${decimalPart}` : ''
      }`;
  } else {
    const padded = (`${nonDecimalPart}`)
      .split('')
      .reverse()
      .join('')
      .match(/.{1,3}/g)
      .map((x) => x
        .split('')
        .reverse()
        .join(''))
      .reverse()
      .join(' ');
    return `${padded}${
      decimalPart && decimalPart.length > 0 ? `.${decimalPart}` : ''
      }`;
  }
};

export const percDiff = (thisYear, lastYear) => ((thisYear - lastYear) / lastYear) * 100;

export const getValidKindsForTimePeriod = (timePeriod, subkindKeeping, kindToDisplay) => {
  if (timePeriod.value === 'compare-this-last-year') {
    // add kinds for this and last year
    return unique(
      Object.getOwnPropertyNames(subkindKeeping['this-year'])
        .filter((k) => subkindKeeping['this-year'][k].has_slaughters)
        .concat(
          Object.getOwnPropertyNames(subkindKeeping['last-year']).filter(
            (k) => subkindKeeping['last-year'][k].has_slaughters,
          ),
        ),
    ).map((k) => ({ value: k, text: kindToDisplay[k] }));
  }
  return Object.getOwnPropertyNames(subkindKeeping[timePeriod.value])
    .filter((k) => subkindKeeping[timePeriod.value][k].has_slaughters)
    .map((k) => ({ value: k, text: kindToDisplay[k] }));
};

export const getValidTimePeriods = (subkindKeeping, allTimePeriods) => {
  const showCompareThisLastYear = subkindKeeping['this-year']?.has_slaughters
      && subkindKeeping['last-year'].has_slaughters;
  return allTimePeriods
    .filter(
      (c) => c.value !== 'compare-this-last-year'
          && subkindKeeping[c.value]?.has_slaughters,
    )
    .concat(
      showCompareThisLastYear
        ? allTimePeriods.find((t) => t.value === 'compare-this-last-year')
        : [],
    );
};

export const getValidSubkindsForTimePeriodAndKind = (timePeriod, kind, subkindKeeping) => {
  if (timePeriod.value === 'compare-this-last-year') {
    if (
      subkindKeeping['this-year'][kind.value].subkinds === {}
        && subkindKeeping['last-year'][kind.value].subkinds === {}
    ) {
      return { value: 'ALL_SUBKINDS', text: 'ALL_SUBKINDS' };
    }

    // add subkinds with slaughters both in this year and last year
    return unique(
      Object.getOwnPropertyNames(
        subkindKeeping['this-year'][kind.value].subkinds,
      )
        .filter((sk) => subkindKeeping['last-year'][kind.value].subkinds[sk]
          .has_slaughters && subkindKeeping['this-year'][kind.value].subkinds[sk]
          .has_slaughters),
    )
      .map((sk) => ({ value: sk, text: sk }))
      .concat({ value: 'ALL_SUBKINDS', text: 'Alle typer' });
  }
  if (
    subkindKeeping[timePeriod.value][kind.value]
      .subkinds === {}
  ) {
    return { value: 'ALL_SUBKINDS', text: 'ALL_SUBKINDS' };
  }
  return Object.getOwnPropertyNames(
    subkindKeeping[timePeriod.value][kind.value]
      .subkinds,
  )
    .filter(
      (sk) => subkindKeeping[timePeriod.value][kind.value]
        .subkinds[sk].has_slaughters,
    )
    .map((sk) => ({ value: sk, text: sk }))
    .concat({ value: 'ALL_SUBKINDS', text: 'Alle typer' });
};

export const timePeriodKindSubkindsForAllFarmerStats = (kindToDisplay, subkindKeeping, timePeriods) => {
  const allTimePeriods = new Set();
  const allKinds = new Set();
  const allSubkinds = new Set();

  const validTimePeriods = getValidTimePeriods(subkindKeeping, timePeriods);
  validTimePeriods.filter((t) => t.value !== 'compare-this-last-year').map((timePeriod) => {
    allTimePeriods.add(timePeriod.value);
    const kindsForTimePeriod = getValidKindsForTimePeriod(timePeriod, subkindKeeping, kindToDisplay);
    kindsForTimePeriod.map((kind) => {
      allKinds.add(kind.value);
      const subkindsForTimePeriod = getValidSubkindsForTimePeriodAndKind(timePeriod, kind, subkindKeeping);
      subkindsForTimePeriod.map((subkind) => {
        allSubkinds.add(subkind.value);
      });
    });
  });

  return {
    allTimePeriods,
    allKinds,
    allSubkinds,
  };
};

export const kindToDisplay = { cattle: 'storfe', sheep: 'småfe', pigs: 'gris' };

export const allTimePeriods = [
  {
    text: 'For siste leveranse',
    value: 'last-settlement',
    timeDescription: 'siste leveranse',
  },
  {
    text: 'Hittil i år',
    value: 'this-year',
    timeDescription: 'hittil i år',
  },
  {
    text: 'For i fjor',
    value: 'last-year',
    timeDescription: 'i periode i fjor',
  },
  {
    text: 'For alle innmeldinger',
    value: 'all-time',
    timeDescription: 'for alle innmeldinger',
  },
  {
    text: 'Sammenliknet i fjor',
    value: 'compare-this-last-year',
    timeDescription: 'sammenliknet i fjor',
  },
];
